import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, RefreshCw, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { regeneratePasscode, deleteCampaign, CAMPAIGN_CAPACITY, bannerLevel } from '../api/a2p.js';

const STATUS_PILL = {
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  pending: 'bg-amber-400/15 text-amber-200 border-amber-400/40',
  'in-review': 'bg-amber-400/15 text-amber-200 border-amber-400/40',
  submitting: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  draft: 'bg-white/10 text-white/60 border-white/20',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  paused: 'bg-white/10 text-white/60 border-white/20',
};

function MeterColor(level) {
  switch (level) {
    case 'critical': return 'bg-rose-500';
    case 'warning': return 'bg-orange-500';
    case 'caution': return 'bg-amber-400';
    default: return 'bg-emerald-500';
  }
}

export default function A2PCampaignCard({ campaign, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const used = campaign.capacity?.used ?? 0;
  const max = campaign.capacity?.max ?? CAMPAIGN_CAPACITY;
  const pct = campaign.capacity?.pct ?? Math.round((used / max) * 100);
  const level = bannerLevel(pct);
  const meterCls = MeterColor(level);
  const status = (campaign.status || 'draft').toLowerCase();
  const statusCls = STATUS_PILL[status] || STATUS_PILL.draft;
  const canDelete = used === 0;

  async function handleRegenerate() {
    setBusy(true);
    try {
      const r = await regeneratePasscode(campaign._id);
      if (r?.ok) {
        toast.success('Passcode regenerated');
        onChange?.();
      }
    } catch (e) {
      toast.error(e.message || 'Regenerate failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!canDelete) {
      toast.error(`Cannot delete — ${used} number(s) attached. Move them first.`);
      return;
    }
    if (!window.confirm('Delete this A2P campaign? Your $10/mo subscription line will be removed at the end of the current period. The $25 setup is non-refundable post-approval.')) {
      return;
    }
    setBusy(true);
    try {
      await deleteCampaign(campaign._id);
      toast.success('Campaign deleted');
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  function copyPasscode() {
    navigator.clipboard?.writeText(campaign.passcode);
    toast.success('Passcode copied');
  }

  return (
    <div className="panel p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyPasscode}
              className="font-mono text-sm text-white inline-flex items-center gap-1.5 hover:text-civix-purple transition"
            >
              {campaign.passcode} <Copy className="w-3 h-3 opacity-60" />
            </button>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={busy}
              className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1 disabled:opacity-40"
              title="Regenerate passcode"
            >
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
            </button>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusCls}`}>
              {status}
            </span>
          </div>
          <div className="text-xs text-white/55 mt-1">
            {campaign.useCaseDescription || campaign.useCase || 'A2P campaign'}
            {' · '}${campaign.monthlyCost || 10}/mo at cost
          </div>
        </div>
        <div className="text-right text-xs text-white/50">
          <div className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {campaign.memberCount || 0}
          </div>
        </div>
      </div>

      {/* Capacity meter */}
      <div>
        <div className="flex items-center justify-between text-xs text-white/65 mb-1.5">
          <span>Numbers attached</span>
          <span className="font-mono">
            {used} / {max} ({pct}%)
          </span>
        </div>
        <div className="h-2 rounded-full bg-black/30 overflow-hidden">
          <div className={`h-full ${meterCls} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Hide details' : 'Show details'}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canDelete || busy}
          className="text-xs text-rose-300 hover:text-rose-200 inline-flex items-center gap-1 disabled:opacity-40"
          title={canDelete ? 'Delete campaign' : 'Cannot delete — numbers attached'}
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>

      {expanded && (
        <div className="pt-3 border-t border-civix-border/40 space-y-3 text-xs text-white/65">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">Use case</div>
            <div>{campaign.useCase || '—'}</div>
          </div>
          {campaign.sampleMessages?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Sample messages</div>
              <ul className="space-y-1 font-mono">
                {campaign.sampleMessages.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
          {campaign.messageFlow && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">CTA / opt-in flow</div>
              <div className="whitespace-pre-wrap">{campaign.messageFlow}</div>
            </div>
          )}
          {campaign.messagingServiceSid && (
            <div className="font-mono text-[11px] text-white/40">MS: {campaign.messagingServiceSid}</div>
          )}
        </div>
      )}
    </div>
  );
}
